import { difference, uniqBy } from 'lodash';
import { mattressPropsReadApi, propsReadApi } from '../../../../api/curator';
import { apiCheckResourceAvailability, getMissingPropsDetails } from '../../../../helpers/api';
import { fetchAllPages } from '../../../../helpers/apiHelper';
import { clearCacheFromAPI, duplicateArrayRemove } from '../../../../helpers/jsHelper';
import { dispatcher } from '../../../../helpers/projectHelper';
import { curatorDebuggerActions } from '../../../../redux/slicers/admin/curatorDebuggerSlicer';
import { setTotalProps } from '../../../../redux/slicers/admin/curatorLoadingProgressBarSlicer';
import { setMissingPropList } from '../../../../redux/slicers/admin/curatorMissingDataSlicer';
import { selectUnityLoaded } from '../../../../redux/slicers/admin/curatorSlicer';
import store, { getAppState } from '../../../../redux/store';
import { simplifyPropData } from './simplifyPropData';
import { LogCurator } from '../../../../helpers/logEvents';
import {
  paginatedProductActions,
  paginatedProductSelectors,
} from '../../../../redux/slicers/admin/paginatedProductSlicer';
import { selectUnityLoaded as selectUnityLoadedConfigurator } from '../../../../redux/slicers/configurator/configurator.slicer';
import { handleMissingCuratorDetails } from './handleMissingCuratorDetails';
import { selectInstantiatedProps } from '../../../../redux/slicers/productBuilder/productBuilder.slicer';
import { errorToast } from '../../../../helpers/toastHelper';
import {
  isMattressBuilderPage,
  isRetailConfiguratorPage,
} from '../../../../components/admin/curator/ProductBuilderComponents/commonHelper';

// loads unity props and sends it to unity as soon as prop data is loaded
// initProp => function which sends prop data to unity -> (prop) => void
export const loadUnityProps = async ({
  initProp,
  unityPropList,
  v1PropListApiData,
  unityLoadedSelector,
  fetchProps,
  groupPropId,
  groupHavingNoAccessWithObjectNames,
  isProductSection = null,
}) => {
  // clearCacheFromAPI();
  const groupPropIds = groupHavingNoAccessWithObjectNames
    ? Object.keys(groupHavingNoAccessWithObjectNames)
    : [];
  if (!unityPropList?.length && !groupPropIds?.length) return;
  const v1PropList = unityPropList.filter((prop) => prop.zipVersion === 1);
  const propList = unityPropList
    .filter((prop) => prop.zipVersion !== 1)
    .map((prop) => ({ ...prop, id: parseInt(prop.id) }));

  // handle normal props
  const combinedPropIds = [...propList.map((p) => p.id), ...groupPropIds].map((a) => +a);
  const propIdList = duplicateArrayRemove(combinedPropIds);
  const hasNormalProps = propIdList.length !== 0;

  let propDetailsList = {};

  if (hasNormalProps) {
    const api = isMattressBuilderPage() ? mattressPropsReadApi : propsReadApi;
    propDetailsList = fetchProps
      ? await fetchProps(propList.map((p) => p.id))
      : await fetchAllPages((page) =>
          api(propIdList, page, isMattressBuilderPage() ? 'is_visible=true' : null)
        );
    if (isMattressBuilderPage()) {
      const fetchedIds = propDetailsList.results.map((p) => p.id);
      const missingIds = difference(propIdList, fetchedIds);
      if (missingIds?.length) {
        const response = await fetchAllPages((page) =>
          mattressPropsReadApi(missingIds, page, 'is_visible=false')
        );
        if (!response?.results?.length) {
          errorToast('Please connect with the support team regarding this project');
        }
        propDetailsList.results = [
          ...(propDetailsList?.results || []),
          ...(response?.results || []),
        ];
      }
    }
  }

  LogCurator('DEBUG_LOG loadUnityProps', {
    propList,
    unityPropList,
    propIdList,
    propDetailsList: propDetailsList?.results,
    groupHavingNoAccessWithObjectNames,
  });

  const missingPropDataList = [];

  if (hasNormalProps && propDetailsList?.results?.length !== propIdList.length) {
    // some of the props are missing
    const fetchedIds = propDetailsList.results.map((p) => p.id);
    const missingIds = difference(propIdList, fetchedIds);
    // fetch info about why prop is missing (unshared or doesn't exist)
    if (missingIds.length) {
      const missingPropsDetails = await getMissingPropsDetails({ idList: missingIds });
      const missing = [...missingPropsDetails?.data?.product].map((prop) => {
        const configProp = unityPropList.find((p) => p.id === prop.id);
        return {
          ...prop,
          name:
            prop.name ||
            configProp?.assetbundle_name ||
            configProp?.main_asset_name ||
            configProp?.object_name ||
            '',
        };
      });
      missingPropDataList.push(...missing);
    }
  }

  // hanlde v1 props
  let v1PropListResponse;
  let v1PropIdList = v1PropList.filter((p) => !p.__MISSING).map((p) => p.oldPropId);
  v1PropIdList = duplicateArrayRemove(v1PropIdList);

  const missingProps = v1PropList
    .filter((p) => p.__MISSING)
    .map((p) => {
      const name = p.assetbundle_name || p.object_name;
      return { name, id: p.oldPropId };
    });

  if (missingProps.length > 0) {
    const oldIds = missingProps.map((p) => p.id);
    const missingPropsDetails = await apiCheckResourceAvailability({ oldPropIdList: oldIds });
    if (missingPropsDetails?.data?.ProductV3) {
      missingPropDataList.push(...missingPropsDetails.data.ProductV3);
    } else {
      missingPropDataList.push(...missingProps.map((p) => ({ name: p.name, is_exist: false })));
    }
  }

  if (v1PropIdList.length) {
    v1PropListResponse = v1PropListApiData; // await propsV1ReadApi(v1PropIdList);
    if (v1PropListResponse.length !== v1PropIdList.length) {
      const fetchedIds = v1PropListResponse.map((p) => p.id);
      const missingIds = difference(v1PropIdList, fetchedIds);

      const missingProps = missingIds.map((id) => {
        const prop = v1PropList.find((p) => p.oldPropId === id);
        const name = prop.assetbundle_name || prop.main_asset_name || prop.object_name;
        return { name, id };
      });

      // TODO: refactor it so we don't repeat the logic
      const oldIds = missingProps.map((p) => p.id);
      const missingPropsDetails = await apiCheckResourceAvailability({ oldPropIdList: oldIds });
      if (missingPropsDetails?.data?.ProductV3) {
        missingPropDataList.push(...missingPropsDetails.data.ProductV3);
      } else {
        missingPropDataList.push(...missingProps.map((p) => ({ name: p.name, is_exist: false })));
      }
    }
  }

  // save missing props
  dispatcher(setMissingPropList(missingPropDataList));

  // prepare props init
  const loadProp = getUnityItemLoader(initProp, unityLoadedSelector, groupPropId);
  const unityDebugArray = [];
  // const loadedIdList = [];

  const uniquePropList = uniqBy(unityPropList, 'id');
  dispatcher(setTotalProps(unityPropList.length));

  await Promise.all(
    uniquePropList.map(async (a) => {
      // if (loadedIdList.includes(a.id)) return;
      // loadedIdList.push(a.id);

      let propDetails;

      if (a.zipVersion === 1) {
        // currently we can not load old props details in one request;
        const oldId = a.oldPropId;
        propDetails = v1PropListResponse?.find((prop) => `${prop.old_product_id}` === `${oldId}`);
      } else {
        propDetails = propDetailsList.results?.find((prop) => Number(prop.id) === Number(a.id));
      }

      let propData;
      if (propDetails) {
        const simplifiedDetails = await simplifyPropData(propDetails);

        propData = {
          id: a.id,
          ...(a?.isRetailAutomationProp ? { isRetailAutomationProp: true } : {}),
          ...(isRetailConfiguratorPage() ? { isProductVariant: propDetails?.is_main_rack || propDetails?.is_product_variant } : {}), // Retail Configurato: here, we are (sending is_main_rack || is_product_variant) because this key tells that it is rack but not a prop
          ...(a?.isMattressBuilderGroupProp ? { isMattressBuilderGroupProp: true } : {}),
          ...(a?.isProductVariant ? { isProductVariant: true } : {}), // this is for only retail automation
          ...simplifiedDetails,
          // ...simplifiedDetailsList.find((prop) => Number(prop.id) === a.id),
        };
      } else {
        // prop details will be missing if prop is missing
        if (a.id) {
          propData = {
            id: a.id,
            locationUrl: 'MISSING',
          };
        } else {
          // debugger;
          console.error('NOT_HANDLED_PROP_MISSING');
        }
      }

      unityDebugArray.push(propData);
      loadProp(propData);
    })
  );

  LogCurator('UNITY_DEBUG ->> PROP LIST', unityDebugArray);
  if (!window.unityDebug) {
    window.unityDebug = { props: [], materialDatas: [] };
  }
  window.unityDebug.props = unityDebugArray;
  LogCurator('UNITY_DEBUG ->> ALL', JSON.parse(JSON.stringify(window.unityDebug)));

  dispatcher(curatorDebuggerActions.setInitialPropList(unityDebugArray));
  await handleMissingCuratorDetails(isProductSection);
};

const propLoadedInUnity = (groupPropId) => {
  const loadingProgress = paginatedProductSelectors.selectItemProgress(getAppState(), groupPropId);

  if (!loadingProgress || !loadingProgress?.total) return;

  dispatcher(
    paginatedProductActions.setProgress({
      ...loadingProgress,
      id: groupPropId,
      loaded: loadingProgress?.loaded + 1,
    })
  );
};

export const getUnityItemLoader = (load, unityLoadedSelector, groupPropId) => {
  // this function will load props immediately or as soon as unity is ready

  const _selectUnityLoaded = unityLoadedSelector || selectUnityLoaded;
  const _selectUnityLoadedConfigurator = selectUnityLoadedConfigurator;

  // unity might not be ready so we save props in this array and will send it to unity as soon as it's ready
  const delayedPropList = [];
  let alreadyHandled = false; // function is async so it might be called few times before unsubscribe will be called
  const unsubscribe = store.subscribe(async () => {
    const unityLoaded = _selectUnityLoaded(getAppState());
    if (unityLoaded && !alreadyHandled) {
      alreadyHandled = true;
      await new Promise((resolve) => setTimeout(resolve, 100)); // wait just in case
      delayedPropList.forEach(load);
      propLoadedInUnity(groupPropId);
      unsubscribe();
    }
  });

  const unityLoadedConfigurator = _selectUnityLoadedConfigurator(getAppState());
  const isBuilder = isMattressBuilderPage();

  return (item) => {
    const unityLoaded = _selectUnityLoaded(getAppState());
    const instantiatedProps = selectInstantiatedProps(getAppState());
    const isPropInstantiated = instantiatedProps.some(
      (val) => parseInt(val?.id) === parseInt(item?.id)
    );
    const restrictDuplicateProps = isBuilder ? isPropInstantiated : false;
    if (unityLoaded || unityLoadedConfigurator) {
      restrictDuplicateProps ? () => {} : load(item);
    } else {
      !isBuilder && delayedPropList.push(item);
    }
    propLoadedInUnity(groupPropId);
  };
};
